import React from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import { Navbar2, Footer2 } from "../../components";
import { getRouteMeta } from "../../seoData";

const ROUTE = "/cases/case-001-cracked-tool-beaconing";

const SUMMARY = [
  ["Scenario", "Download from a local web server, execution, simulated C2 beacon"],
  ["Affected system", "Domain-joined Windows 11 client"],
  ["Evidence sources", "Loki logs, RAM capture, disk image"],
  ["Tools", "FTK Imager, Volatility 3, Autopsy, Kali"],
  ["Analyst", "Javian Anderson"],
  ["Status", "Planned, not yet run"],
];

const DOCS = [
  {
    kind: "PDF",
    name: "Incident report",
    note: "Findings, timeline, and conclusion, written for a non-technical reader",
  },
  {
    kind: "PDF",
    name: "Chain of custody form",
    note: "Who handled each item, and when",
  },
  {
    kind: "PDF",
    name: "Incident response form",
    note: "Detection, containment, and recovery steps",
  },
  {
    kind: "TXT",
    name: "Evidence hash list",
    note: "SHA-256 for every image and capture",
  },
];

const EVIDENCE = [
  ["E-001", "Disk image, client C:"],
  ["E-002", "Memory capture"],
  ["E-003", "Loki log export"],
];

const STEPS = [
  [
    "Scenario and setup",
    "What was planted, where it was hosted, and what the exercise is meant to simulate.",
  ],
  [
    "Detection from logs",
    "What Loki showed first: process creation, the web download, the outbound connection.",
  ],
  [
    "Acquisition",
    "Memory capture first, then the disk image, with hashes verified before and after.",
  ],
  [
    "Analysis",
    "Volatility for processes and network, Autopsy for files and artifacts, event logs for the user session.",
  ],
  ["Timeline", "One merged timeline across logs, memory, and disk."],
  [
    "Findings and lessons",
    "What was found, how confident it is, and what detection would have caught it sooner.",
  ],
];

function Case001() {
  const { title, description: desc, image, url } = getRouteMeta(ROUTE);

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} data-react-helmet="true"></meta>

        <meta property="og:type" content="website" data-react-helmet="true"></meta>
        <meta property="og:url" content={url} data-react-helmet="true"></meta>
        <meta property="og:title" content={title} data-react-helmet="true"></meta>
        <meta property="og:description" content={desc} data-react-helmet="true"></meta>
        <meta property="og:image" content={image} data-react-helmet="true"></meta>

        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta property="twitter:url" content={url} data-react-helmet="true"></meta>
        <meta property="twitter:title" content={title} data-react-helmet="true"></meta>
        <meta property="twitter:description" content={desc} data-react-helmet="true"></meta>
        <meta property="twitter:image" content={image} data-react-helmet="true"></meta>
      </Helmet>
      <Navbar2 />
      <section id="blogs">
        <div className="blogs main">
          <div className="blogs-header pb-4">
            <Link to="/cases/" className="blog-back-link">
              <i className="fa-solid fa-arrow-left"></i>&nbsp;Back to Cases
            </Link>
            <h2 className="section-title">
              Case 001: Simulated Cracked-Tool Download and C2 Beaconing
            </h2>
            <div className="blog-meta">
              <span>
                <i className="fa-solid fa-folder-open"></i>Case 001
              </span>
              <span>
                <i className="fa-solid fa-flask"></i>Lab exercise
              </span>
              <span>
                <i className="fa-solid fa-hourglass-half"></i>Planned
              </span>
            </div>
          </div>

          <p className="m-5">
            A user downloads what looks like a cracked utility. It runs, then
            phones home. What does the evidence show, and how quickly can it be
            found?
          </p>

          <dl className="case-summary">
            {SUMMARY.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>

          <h3>Case Documents</h3>
          <div className="case-docs">
            {DOCS.map((d) => (
              <div key={d.name} className="case-doc">
                <div className="case-doc__ico">{d.kind}</div>
                <div className="case-doc__meta">
                  <b>{d.name}</b>
                  <small>{d.note}</small>
                </div>
                <span className="case-doc__state">Available at case close</span>
              </div>
            ))}
          </div>

          <h3>Evidence Integrity</h3>
          <div className="case-table-wrap">
            <table className="case-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Description</th>
                  <th>SHA-256</th>
                </tr>
              </thead>
              <tbody>
                {EVIDENCE.map(([item, what]) => (
                  <tr key={item}>
                    <td>{item}</td>
                    <td>{what}</td>
                    <td>
                      <code>pending</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Walkthrough</h3>
          <ol className="case-steps">
            {STEPS.map(([name, text]) => (
              <li key={name}>
                <b>{name}</b>
                <span>{text}</span>
              </li>
            ))}
          </ol>

          <h3>Related</h3>
          <div className="case-related">
            <Link to="/digital-forensics-homelab-logging/">
              HomeLab Part 6: Centralized Logging
            </Link>
            <Link to="/digital-forensics-homelab-gpos/">
              HomeLab Part 5: Group Policy Objects
            </Link>
            <Link to="/digital-forensics-homelab-topology/">
              HomeLab Part 2: Network Topology
            </Link>
          </div>
        </div>
        <Footer2 />
      </section>
    </div>
  );
}

export default Case001;
